/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/Skills.tsx
 * ----------------------------------------------------------------------------
 * Catálogo de Skills (habilidades/certificações) do Workforce.
 * - Lista com busca, adicionar, editar inline (code/name/is_active) e remover.
 * - Usa API DRF: /api/workforce/skills/
 *   GET (lista, ?search=&ordering=&page_size=), POST, PUT, DELETE
 * - Visual alinhado ao padrão (fundo escuro / contraste).
 * ========================================================================== */

import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";

type Skill = {
    id?: number;
    code?: string;
    name?: string;
    is_active?: boolean;
};

type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[] };

function cls(...xs: Array<string | false | null | undefined>) {
    return xs.filter(Boolean).join(" ");
}

async function fetchSkills(term: string) {
    const { data } = await apiGet<Paginated<Skill> | Skill[]>("/workforce/skills/", {
        params: {
            search: term || undefined,
            ordering: "code",
            page_size: 500,
        },
    });

    const items: Skill[] = Array.isArray(data) ? data : data?.results ?? [];
    const total = Array.isArray(data) ? items.length : data?.count ?? items.length;
    return { items, total };
}

export default function WorkforceSkills() {
    const qc = useQueryClient();
    const [term, setTerm] = useState("");
    const [adding, setAdding] = useState<Skill | null>(null);
    const [savingId, setSavingId] = useState<number | "new" | null>(null);

    const q = useQuery({
        queryKey: ["wf-skills", term],
        queryFn: () => fetchSkills(term),
        staleTime: 30_000,
    });

    const rows = useMemo(() => q.data?.items ?? [], [q.data]);

    // --- Ações ---

    async function onToggleActive(row: Skill) {
        if (!row.id) return;
        try {
            setSavingId(row.id);
            await apiPut(`/workforce/skills/${row.id}/`, {
                code: row.code ?? "",
                name: row.name ?? "",
                is_active: !row.is_active,
            });
            await qc.invalidateQueries({ queryKey: ["wf-skills"] });
        } finally {
            setSavingId(null);
        }
    }

    async function onBlurUpdateField(row: Skill, field: "code" | "name", value: string) {
        if (!row.id) return;
        const trimmed = value ?? "";
        if (trimmed === (row[field] ?? "")) return; // nada mudou
        try {
            setSavingId(row.id);
            await apiPut(`/workforce/skills/${row.id}/`, {
                code: field === "code" ? trimmed : (row.code ?? ""),
                name: field === "name" ? trimmed : (row.name ?? ""),
                is_active: !!row.is_active,
            });
            await qc.invalidateQueries({ queryKey: ["wf-skills"] });
        } finally {
            setSavingId(null);
        }
    }

    function startAdd() {
        setAdding({ code: "", name: "", is_active: true });
    }

    async function saveAdd() {
        if (!adding) return;
        try {
            setSavingId("new");
            const payload = {
                code: (adding.code ?? "").trim(),
                name: (adding.name ?? "").trim(),
                is_active: !!adding.is_active,
            };
            await apiPost("/workforce/skills/", payload);
            setAdding(null);
            await qc.invalidateQueries({ queryKey: ["wf-skills"] });
        } finally {
            setSavingId(null);
        }
    }

    function cancelAdd() {
        setAdding(null);
    }

    async function removeRow(row: Skill) {
        if (!row.id) return;
        const ok = window.confirm(`Remover skill "${row.code ?? row.name ?? row.id}"?`);
        if (!ok) return;
        try {
            setSavingId(row.id);
            await apiDelete(`/workforce/skills/${row.id}/`);
            await qc.invalidateQueries({ queryKey: ["wf-skills"] });
        } finally {
            setSavingId(null);
        }
    }

    return (
        <div className="p-4">
            {/* Header */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Workforce • Skills (catálogo)</h2>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Cadastro de habilidades/certificações disponíveis para uso no Workforce.
                    </p>
                </div>

                <div className="flex gap-2">
                    <input
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder="Buscar por código ou nome..."
                        className="h-9 w-72 rounded-md border border-neutral-300 px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-900"
                    />
                    <button
                        onClick={() => q.refetch()}
                        className="h-9 rounded-md border border-neutral-300 px-3 text-sm dark:border-neutral-700"
                    >
                        Atualizar
                    </button>
                    <button
                        onClick={startAdd}
                        className="h-9 rounded-md border border-neutral-300 px-3 text-sm dark:border-neutral-700"
                        disabled={!!adding}
                        title="Adicionar nova skill"
                    >
                        Adicionar
                    </button>
                </div>
            </div>

            {/* Estados */}
            {q.isLoading && (
                <div className="rounded-md border border-neutral-200 p-6 dark:border-neutral-800">
                    Carregando skills…
                </div>
            )}

            {q.isError && (
                <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                    {(q.error as Error)?.message || "Erro ao carregar skills."}
                </div>
            )}

            {/* Tabela */}
            {!q.isLoading && !q.isError && (
                <div className="overflow-auto rounded-md border border-neutral-200 dark:border-neutral-800">
                    <table className="min-w-[760px] w-full text-sm">
                        <thead className="bg-neutral-50 dark:bg-neutral-900">
                            <tr className="text-left">
                                <Th style={{ width: 160 }}>Código</Th>
                                <Th>Nome</Th>
                                <Th style={{ width: 120 }}>Ativo</Th>
                                <Th style={{ width: 140 }}>Ações</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Linha de adição */}
                            {adding && (
                                <tr className="border-t border-neutral-100 dark:border-neutral-800">
                                    <Td>
                                        <input
                                            autoFocus
                                            className="input input-sm w-full dark:bg-neutral-900"
                                            value={adding.code ?? ""}
                                            onChange={(e) => setAdding((s) => ({ ...(s ?? {}), code: e.target.value }))}
                                            placeholder="Ex.: FORKLIFT"
                                        />
                                    </Td>
                                    <Td>
                                        <input
                                            className="input input-sm w-full dark:bg-neutral-900"
                                            value={adding.name ?? ""}
                                            onChange={(e) => setAdding((s) => ({ ...(s ?? {}), name: e.target.value }))}
                                            placeholder="Ex.: Conduzir empilhador"
                                        />
                                    </Td>
                                    <Td>
                                        <label className="inline-flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={!!adding.is_active}
                                                onChange={(e) =>
                                                    setAdding((s) => ({ ...(s ?? {}), is_active: e.target.checked }))
                                                }
                                            />
                                            <span className="text-xs text-neutral-500">ativo</span>
                                        </label>
                                    </Td>
                                    <Td>
                                        <div className="flex gap-2">
                                            <button
                                                className="btn btn-primary btn-xs"
                                                onClick={saveAdd}
                                                disabled={savingId === "new"}
                                            >
                                                {savingId === "new" ? "Salvando…" : "Salvar"}
                                            </button>
                                            <button className="btn btn-xs btn-outline" onClick={cancelAdd}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            )}

                            {/* Linhas existentes */}
                            {rows.length === 0 && !adding && (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="p-6 text-center text-neutral-500 dark:text-neutral-400"
                                    >
                                        Nenhuma skill encontrada.
                                    </td>
                                </tr>
                            )}

                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-neutral-100 dark:border-neutral-800">
                                    <Td>
                                        <input
                                            className="input input-sm w-full dark:bg-neutral-900"
                                            defaultValue={r.code ?? ""}
                                            onBlur={(e) => onBlurUpdateField(r, "code", e.target.value)}
                                            placeholder="Ex.: FORKLIFT"
                                        />
                                    </Td>
                                    <Td>
                                        <input
                                            className="input input-sm w-full dark:bg-neutral-900"
                                            defaultValue={r.name ?? ""}
                                            onBlur={(e) => onBlurUpdateField(r, "name", e.target.value)}
                                            placeholder="Ex.: Conduzir empilhador"
                                        />
                                    </Td>
                                    <Td>
                                        <label className="inline-flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={!!r.is_active}
                                                onChange={() => onToggleActive(r)}
                                                disabled={savingId === r.id}
                                            />
                                            <span className="text-xs text-neutral-500">ativo</span>
                                        </label>
                                    </Td>
                                    <Td>
                                        <button
                                            className="btn btn-xs btn-outline"
                                            onClick={() => removeRow(r)}
                                            disabled={savingId === r.id}
                                        >
                                            Remover
                                        </button>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!q.isLoading && !q.isError && (
                <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                    {rows.length} itens
                </div>
            )}
        </div>
    );
}

// -------------------------- Subcomponentes de tabela -------------------------
function Th({
    children,
    className = "",
    ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
    return (
        <th
            className={cls("px-3 py-2 text-xs font-semibold uppercase tracking-wide", className)}
            {...rest}
        >
            {children}
        </th>
    );
}

function Td({
    children,
    className = "",
    ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
    return (
        <td className={cls("px-3 py-2 align-top", className)} {...rest}>
            {children}
        </td>
    );
}
